import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import Button from "components/basic/button";
import DatePicker from "components/basic/datePicker";
import Typography from "components/basic/typography";
import UploadFile from "components/basic/uploadFile";
import { useS3 } from "hooks/useS3";
import {
  formatIsoDate,
  todayIsoDate,
  validateExpiryDate,
} from "pages/formTools";
import { Vendor } from "pages/userAuthentication/signUp";
import DocumentCard from "../DocumentCard";

interface InsuranceSectionProps {
  vendor: Vendor;
  onSave: (updates: Record<string, string>) => Promise<boolean>;
}

/** The insurance certificate and when it expires, renewed once a year. */
const InsuranceSection: React.FC<InsuranceSectionProps> = ({
  vendor,
  onSave,
}) => {
  const { uploadToS3 } = useS3();
  const { t } = useTranslation("profile");
  // Vendors who registered before the date was collected must add it.
  const [editing, setEditing] = useState(!vendor.insuranceExpiryDate);
  const [file, setFile] = useState<File | null>(null);
  const [expiryDate, setExpiryDate] = useState(vendor.insuranceExpiryDate);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const dateError = validateExpiryDate({
      name: t("insurance.expiry_date"),
      value: expiryDate,
      required: true,
    });
    if (dateError) {
      setError(dateError);
      return;
    }

    setSaving(true);
    const url = file ? await uploadToS3(file, "proof-of-insurance") : null;
    if (file && !url) {
      setSaving(false);
      return;
    }
    const saved = await onSave({
      insuranceExpiryDate: expiryDate,
      ...(url ? { proofOfInsuranceURL: url } : {}),
    });
    setSaving(false);
    if (saved) {
      setFile(null);
      setEditing(false);
    }
  };

  return (
    <DocumentCard
      title={t("insurance.title")}
      rows={[
        {
          label: t("insurance.expiry"),
          value:
            formatIsoDate(vendor.insuranceExpiryDate) || t("documents.not_set"),
        },
      ]}
      documentUrl={vendor.proofOfInsuranceURL}
      editing={editing}
      onEdit={() => setEditing(true)}
    >
      <Typography variant="body2" style={{ opacity: 0.7 }}>
        {t("insurance.hint")}
      </Typography>
      <UploadFile
        label={t("insurance.upload")}
        onChange={setFile}
        file={file}
      />
      <Typography variant="body2">{t("insurance.expiry_date")}</Typography>
      <DatePicker
        value={expiryDate}
        minDate={todayIsoDate()}
        onChange={(date) => {
          setExpiryDate(date);
          setError(null);
        }}
        error={error ?? undefined}
      />
      <Box display="flex" gap={1} justifyContent="flex-end">
        {vendor.insuranceExpiryDate && (
          <Button variant="text" onClick={() => setEditing(false)}>
            {t("cancel")}
          </Button>
        )}
        <Button
          variant="contained"
          color="warning"
          style={{ color: "white" }}
          onClick={handleSave}
          loading={saving}
          disabled={saving}
        >
          {t("save")}
        </Button>
      </Box>
    </DocumentCard>
  );
};

export default InsuranceSection;
